import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useIsPhone from "features/files/use-is-phone";
import { ProductDetails } from "types/ProductCatalogue";
import { formatQuantity } from "./product-format";

interface Column {
  title: string;
  isRight?: boolean;
}

function TableCard({ columns, children }: { columns: Column[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
          <tr>
            {columns.map((column) => (
              <th key={column.title} className={`px-4 py-2.5 font-semibold ${column.isRight ? "text-right" : "text-left"}`}>
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">{children}</tbody>
      </table>
    </div>
  );
}

function EmptyCard({ text }: { text: string }) {
  return <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-400">{text}</div>;
}

export function StockTable({ stock }: { stock: ProductDetails["stock"] }) {
  const { t } = useTranslation();
  if (stock.length === 0) return <EmptyCard text={t("products_page_no_stock")} />;

  const total = stock.reduce((sum, row) => sum + (row.quantity || 0), 0);
  return (
    <TableCard columns={[{ title: t("warehouse") }, { title: t("title") }, { title: t("quantity"), isRight: true }]}>
      {stock.map((row) => (
        <tr key={row.id}>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.warehouse?.name}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.name}</td>
          <td className="px-4 py-2.5 text-right tabular-nums">{formatQuantity(row.quantity)}</td>
        </tr>
      ))}
      <tr className="bg-gray-50 font-semibold">
        <td className="px-4 py-2.5" colSpan={2}>
          {t("total")}
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums">{formatQuantity(total)}</td>
      </tr>
    </TableCard>
  );
}

export function ReservationsTable({ reservations }: { reservations: ProductDetails["reservations"] }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  if (reservations.length === 0) return <EmptyCard text={t("products_page_no_reservations")} />;

  // A phone has no room for five columns: the description and the user move under the title.
  const columns = isPhone
    ? [{ title: t("title") }, { title: t("customer") }, { title: t("quantity"), isRight: true }]
    : [
        { title: t("title") },
        { title: t("description") },
        { title: t("customer") },
        { title: t("user") },
        { title: t("quantity"), isRight: true },
      ];
  return (
    <TableCard columns={columns}>
      {reservations.map((item) => {
        const reservation = item.reservation;
        const detailsUnderTitle = [reservation?.description, reservation?.user?.name].filter(Boolean).join(" · ");
        return (
          <tr key={item.id}>
            <td className="px-4 py-2.5 font-medium text-gray-900">
              {reservation?.title}
              {isPhone && detailsUnderTitle && <p className="text-xs font-normal text-gray-500">{detailsUnderTitle}</p>}
            </td>
            {!isPhone && <td className="px-4 py-2.5 text-gray-600">{reservation?.description}</td>}
            <td className="px-4 py-2.5 text-gray-900">{reservation?.customer?.name}</td>
            {!isPhone && <td className="px-4 py-2.5 text-gray-600">{reservation?.user?.name}</td>}
            <td className="px-4 py-2.5 text-right font-medium tabular-nums">{formatQuantity(item.quantity)}</td>
          </tr>
        );
      })}
    </TableCard>
  );
}

export function MetaTable({ meta }: { meta: ProductDetails["meta"] }) {
  const { t } = useTranslation();
  return (
    <TableCard columns={[{ title: t("products_page_meta_field") }, { title: t("products_page_meta_value") }]}>
      {Object.entries(meta).map(([key, value]) => (
        <tr key={key}>
          <td className="w-1/3 px-4 py-2 font-mono text-xs text-gray-500">{key}</td>
          <td className="px-4 py-2 text-gray-800">{typeof value === "object" ? JSON.stringify(value) : String(value)}</td>
        </tr>
      ))}
    </TableCard>
  );
}
